import React from 'react';

/**
 * Modern KPI metric card with clean Zara-style aesthetics
 * Props:
 *   label: string
 *   value: string | number
 *   sub: string | ReactNode
 *   trend: 'up' | 'down' | 'neutral'
 *   accentClass: string
 *   icon: ReactNode
 *   loading: boolean
 */
const KPICard = ({
    label,
    value,
    sub,
    trend,
    accentClass = 'bg-stone-900 text-white',
    icon,
    loading = false,
}) => {
    const getTrendBadge = () => {
        if (!trend && !sub) return null;
        const trendBg =
            trend === 'up'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200/60'
                : trend === 'down'
                ? 'bg-rose-50 text-rose-700 border-rose-200/60'
                : 'bg-stone-100 text-stone-600 border-stone-200';

        const arrow = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '';

        return (
            <span
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${trendBg}`}
            >
                {arrow && <span>{arrow}</span>}
                <span className="truncate max-w-[120px]">{sub}</span>
            </span>
        );
    };

    return (
        <div className="bg-white rounded-2xl border border-stone-200/70 p-5 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between group min-w-0">
            <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
                <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 ${accentClass}`}
                >
                    {icon}
                </div>
                <div className="min-w-0 flex-shrink-0">{getTrendBadge()}</div>
            </div>
            <div className="min-w-0">
                <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-1 truncate">
                    {label}
                </p>
                {loading ? (
                    <div className="h-8 w-28 bg-stone-100 animate-pulse rounded-lg mt-1" />
                ) : (
                    <h3 className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight leading-tight truncate">
                        {value}
                    </h3>
                )}
            </div>
        </div>
    );
};

export default KPICard;
